/**
 * Step gating for the guided-tour engine (REQ D4-ONB-004).
 *
 * A step is visible only when ALL of its declared gates pass — the SAME
 * predicate shape CrmSidebar's ``itemVisible`` uses (module enabled + flag held
 * + any-of flags). Gating here means a tour never highlights a surface the user
 * cannot open, and a tour whose steps ALL filter out never offers itself
 * (``isTourAvailable`` is false), so CUSTOM/back-office presets degrade
 * gracefully instead of dead-ending.
 */
import type {
  TourAudience,
  TourDefinition,
  TourGateContext,
  TourStep,
} from "./types";

/**
 * True if every declared gate on the step passes for the current user. The
 * ``excludeFlag`` gate is the INVERSE of ``permFlag`` (issue #1522): the step is
 * hidden when the user HOLDS the named flag, so two mutually-exclusive variants
 * of a step never both surface. ``adminOnly`` requires an admin-role user.
 */
export function stepVisible(step: TourStep, gate: TourGateContext): boolean {
  return (
    (!step.module || gate.isModuleEnabled(step.module)) &&
    (!step.permFlag || gate.has(step.permFlag)) &&
    (!step.anyFlag || step.anyFlag.some((f) => gate.has(f))) &&
    (!step.excludeFlag || !gate.has(step.excludeFlag)) &&
    (!step.adminOnly || Boolean(gate.isAdmin))
  );
}

/** The steps of a tour that are visible to the current user, in order. */
export function visibleSteps(
  tour: TourDefinition,
  gate: TourGateContext,
): TourStep[] {
  return tour.steps.filter((step) => stepVisible(step, gate));
}

/**
 * True when the WHOLE-TOUR audience gate passes for the current user (issue
 * #1347). Every declared facet must hold (logical AND), mirroring a step's
 * gates and CrmSidebar's ``itemVisible``. An undefined audience always passes
 * (backward-compatible — agent section tours keep their prior behaviour).
 *
 * ``adminOnly`` checks ``gate.isAdmin``; ``preset`` matches ``gate.preset``
 * against the allowed preset(s); ``excludePreset`` is the INVERSE -- it WITHHOLDS
 * the tour from a user whose preset is in the deny-list (issue #1528), while a
 * null/undefined preset (an admin, or a gate that omits preset) is never
 * excluded; ``flag`` / ``module`` reuse the same predicates a step uses. A gate
 * context that does not supply ``isAdmin`` / ``preset`` (older callers) treats
 * those require-style facets as NOT satisfied, so an ``adminOnly`` / ``preset``
 * tour is conservatively withheld rather than wrongly offered.
 */
export function audienceVisible(
  audience: TourAudience | undefined,
  gate: TourGateContext,
): boolean {
  if (!audience) return true;
  if (audience.adminOnly && !gate.isAdmin) return false;
  if (audience.preset) {
    const allowed = Array.isArray(audience.preset)
      ? audience.preset
      : [audience.preset];
    if (!gate.preset || !allowed.includes(gate.preset)) return false;
  }
  // excludePreset (issue #1528): the inverse of preset -- withhold the tour from
  // a user ON one of these presets. A null/undefined preset (admin or
  // preset-less gate) is NOT excluded, so the agent section tours still surface
  // for admins and for a plain agent whose preset is "none"/null.
  if (
    audience.excludePreset &&
    gate.preset &&
    audience.excludePreset.includes(gate.preset)
  ) {
    return false;
  }
  if (audience.flag && !gate.has(audience.flag)) return false;
  if (audience.module && !gate.isModuleEnabled(audience.module)) return false;
  return true;
}

/**
 * A tour offers itself only when its whole-tour AUDIENCE gate passes (issue
 * #1347) AND at least one of its steps is visible. The audience gate stops an
 * admin / back-office desk tour — whose ungated ``center`` first step would
 * otherwise pass for anyone — from leaking to a plain agent who can never reach
 * that surface, while an un-audienced tour keeps the prior "any visible step"
 * behaviour.
 */
export function isTourAvailable(
  tour: TourDefinition,
  gate: TourGateContext,
): boolean {
  return audienceVisible(tour.audience, gate) && visibleSteps(tour, gate).length > 0;
}

/**
 * Index of the first step belonging to the NEXT section after ``fromIndex``,
 * or ``-1`` when there is no later section. Used by the tooltip's
 * "Skip to next section" control (REQ D4-ONB-003). Steps without an explicit
 * ``section`` are treated as their own boundary (id-based) so a flat tour still
 * advances one step at a time.
 */
export function nextSectionIndex(steps: TourStep[], fromIndex: number): number {
  const current = steps[fromIndex];
  if (!current) return -1;
  const currentSection = current.section ?? current.id;
  for (let i = fromIndex + 1; i < steps.length; i += 1) {
    const step = steps[i];
    if (!step) break;
    const section = step.section ?? step.id;
    if (section !== currentSection) return i;
  }
  return -1;
}
